import { useUiStore } from '../../features/settings/ui-store.js';

import type { ComposerAttachment } from './composer/index.js';
import {
  survivesSubmitRedirect,
  toOutgoingAttachments,
} from './outgoing-attachments.js';

/**
 * Stage a home-screen submit for the `/chats` route, which sends it from an
 * effect keyed on `pendingDraft`. The draft is always written, even when it is
 * empty: a file-only submit that left the draft at null did not change the key,
 * so a second file-only submit while `/chats` was already mounted never fired
 * and the message was lost with its chips already cleared.
 *
 * A skill-only submit (no text, no files) is staged too: picking a skill and pressing Send runs it.
 *
 * Uploads still in flight are dropped, because the redirect that follows a
 * home-screen submit loses their bytes. Returns whether anything was staged.
 */
export function stageLauncherSubmit(
  body: string,
  attachments: ComposerAttachment[],
  skillIds: string[],
): boolean {
  const text = body.trim();
  const ready = toOutgoingAttachments(
    attachments.filter(survivesSubmitRedirect),
  );
  if (!text && ready.length === 0 && skillIds.length === 0) return false;
  const state = useUiStore.getState();
  state.setPendingDraft(text);
  /**
   * Written unconditionally: a staged submit describes itself completely. Skipping the empty case
   * let a second launch inherit the first one's chips whenever `/chats` had not consumed them yet
   * — two quick-action cards clicked in a row sent the second prompt with the first card's skills.
   */
  state.setPendingSkillIds(skillIds);
  state.setPendingAttachments(ready);
  return true;
}
